import React, { useState } from 'react';
import { X, ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import { api } from '@/utils/api';

// ─────────────────────────────────────────────────────────────────────────────
// Universal Book-a-Demo modal — light theme, full enterprise lead form.
// Used from Landing, TopNavMega, ProductPage, ContentPage.
// Saves to MongoDB demo_requests collection (visible in Admin → CRM Requests).
// ─────────────────────────────────────────────────────────────────────────────
const COMPANY_TYPES = [
  'Property management company (PMC)',
  'Property owner / Landlord',
  'Real estate developer',
  'Institutional operator',
  'Investment firm / Asset manager',
  'Vendor / Contractor',
  'Other',
];

const ROLES = ['CEO / Owner', 'COO / Operations Director', 'Asset / Property Manager', 'Finance / Accounting', 'Investments', 'IT / Procurement', 'Other'];

const PORTFOLIOS = ['1 – 10 units', '10 – 50 units', '50 – 200 units', '200 – 1,000 units', '1,000+ units', 'Mega development'];

const CONTACT_METHODS = ['Email', 'Phone call', 'WhatsApp'];

export default function BookDemoModal({ open, onClose, lang = 'en', sourcePage }) {
  const isAr = lang === 'ar';
  const [form, setForm] = useState({
    name: '', company: '', email: '', phone: '',
    role: ROLES[0], company_type: COMPANY_TYPES[0],
    portfolio_size: PORTFOLIOS[1], preferred_contact: CONTACT_METHODS[0],
    message: '',
  });
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState('');

  if (!open) return null;

  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      await api.submitDemoRequest({
        ...form,
        request_type: 'demo',
        source_page: sourcePage || (typeof window !== 'undefined' ? window.location.pathname : 'landing'),
      });
      setOk(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Could not submit. Please try again.');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[#0E1320]/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} dir={isAr ? 'rtl' : 'ltr'}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-2xl bg-white shadow-2xl" data-testid="book-demo-modal">
        <div className="sticky top-0 bg-white border-b border-[#F1F4F7] px-6 py-4 flex items-start justify-between">
          <div>
            <div className="text-[10.5px] reva-mono uppercase tracking-[0.18em] text-[#71777E]">{isAr ? 'احجز عرضًا' : 'Book a demo'}</div>
            <h2 className="mt-1 text-xl font-semibold text-[#0E1320]">{isAr ? 'تحدّث مع فريق REVA' : "Let's talk operations"}</h2>
          </div>
          <button onClick={onClose} className="text-[#71777E] hover:text-[#0E1320]" data-testid="book-demo-close" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {ok ? (
          <div className="px-6 py-10 text-center" data-testid="book-demo-success">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
            <h3 className="mt-4 text-xl font-semibold text-[#0E1320]">{isAr ? 'وصلتنا طلبيّتك' : 'Got it. Talk soon.'}</h3>
            <p className="mt-2 text-[13.5px] text-[#4A5263]">
              {isAr
                ? 'سيتواصل أحد أعضاء فريق REVA معك خلال 24 ساعة عمل.'
                : 'A member of the REVA team will reach out within 1 business day.'}
            </p>
            <button onClick={onClose} className="mt-6 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#0E1320] text-white text-[13px] font-semibold hover:bg-[#1A2238]">
              {isAr ? 'إغلاق' : 'Close'}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-6 py-5 space-y-3">
            <Row>
              <Field label={isAr ? 'الاسم الكامل' : 'Full name'}    required value={form.name}    onChange={update('name')}    testid="bdm-name" />
              <Field label={isAr ? 'الشركة' : 'Company'}             required value={form.company} onChange={update('company')} testid="bdm-company" />
            </Row>
            <Row>
              <Field label={isAr ? 'البريد الإلكترونيّ' : 'Work email'} type="email" required value={form.email} onChange={update('email')} testid="bdm-email" />
              <Field label={isAr ? 'الجوّال' : 'Phone'}              type="tel"   value={form.phone}   onChange={update('phone')}   testid="bdm-phone"   placeholder="+9665XXXXXXXX" />
            </Row>
            <Row>
              <Select label={isAr ? 'الدور الوظيفيّ' : 'Your role'}     value={form.role}          onChange={update('role')}          opts={ROLES}          testid="bdm-role" />
              <Select label={isAr ? 'نوع الجهة' : 'Company type'}       value={form.company_type}  onChange={update('company_type')}  opts={COMPANY_TYPES}  testid="bdm-company-type" />
            </Row>
            <Row>
              <Select label={isAr ? 'حجم المحفظة' : 'Portfolio size'}    value={form.portfolio_size} onChange={update('portfolio_size')} opts={PORTFOLIOS}      testid="bdm-portfolio" />
              <Select label={isAr ? 'التواصل المفضّل' : 'Preferred contact'} value={form.preferred_contact} onChange={update('preferred_contact')} opts={CONTACT_METHODS} testid="bdm-contact-method" />
            </Row>
            <label className="block">
              <span className="text-[12px] text-[#4A5263]">{isAr ? 'رسالتك (اختياريّ)' : 'Anything we should know? (optional)'}</span>
              <textarea value={form.message} onChange={update('message')} rows={3} data-testid="bdm-message"
                className="mt-1 w-full px-3 py-2 rounded-xl border border-[#E6EAF0] bg-white text-[13.5px] text-[#0E1320] placeholder-[#9CA3AF] focus:outline-none focus:border-[#0E1320]" />
            </label>
            {err && <div className="text-[12px] text-red-600">{err}</div>}
            <button disabled={busy || !form.name || !form.email || !form.company} type="submit" data-testid="bdm-submit"
              className="w-full mt-2 inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-[#00734A] hover:bg-[#005F3D] text-white font-semibold text-[14px] disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{isAr ? 'إرسال الطلب' : 'Submit request'} <ArrowRight className="h-4 w-4" /></>}
            </button>
            <p className="text-[11px] text-[#71777E] mt-1">
              {isAr
                ? 'يحفظ هذا الطلب في مركز عملاء REVA. لا نرسل لك بريدًا تسويقيًّا.'
                : 'Goes straight to the REVA CRM. We never use it for marketing.'}
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

function Row({ children }) { return <div className="grid sm:grid-cols-2 gap-3">{children}</div>; }

function Field({ label, value, onChange, type = 'text', required, placeholder, testid }) {
  return (
    <label className="block">
      <span className="text-[12px] text-[#4A5263]">{label}{required && <span className="text-red-500"> *</span>}</span>
      <input type={type} value={value} onChange={onChange} required={required} placeholder={placeholder} data-testid={testid}
        className="mt-1 w-full h-10 px-3 rounded-xl border border-[#E6EAF0] bg-white text-[13.5px] text-[#0E1320] placeholder-[#9CA3AF] focus:outline-none focus:border-[#0E1320]" />
    </label>
  );
}

function Select({ label, value, onChange, opts, testid }) {
  return (
    <label className="block">
      <span className="text-[12px] text-[#4A5263]">{label}</span>
      <select value={value} onChange={onChange} data-testid={testid}
        className="mt-1 w-full h-10 px-3 rounded-xl border border-[#E6EAF0] bg-white text-[13.5px] text-[#0E1320] focus:outline-none focus:border-[#0E1320]">
        {opts.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
